/**
 * Fetch policy for the Audit Spend dashboard.
 *
 * Lives in its own module (rather than inline in `AuditSpend.tsx`) so it can be
 * asserted by a test without dragging the whole page — and its charts, icons
 * and API client — into the test environment.
 *
 * Why there is no polling: this page reads BigQuery. Measured by dry-run on
 * 2026-08-02, one full refresh of its nine routes scans ~790 MB (~$0.005 at
 * $6.25/TB). On the 60s interval the page originally shipped with, a tab left
 * open on a second monitor cost ~$52/month and learned almost nothing — every
 * section is a daily rollup or trailing-window aggregate in which only the
 * current day can move. Cost should accrue when someone looks at the page, not
 * while it sits open.
 */

/**
 * How long a result is treated as current — matched to the API's 24h KV cache.
 *
 * This is load-bearing, not a nicety: `refetchOnWindowFocus` without a
 * meaningful `staleTime` is *worse* than the polling it replaces, because
 * someone alt-tabbing twice a minute would out-scan a 60s interval.
 *
 * It matches `KV_TTL_SEC` on the server deliberately. A shorter client value
 * would fire requests the server just answers from cache — harmless but
 * pointless noise; a longer one would keep showing a payload the server has
 * already let expire. Both tiers agreeing on one number is what makes "up to
 * a day old" a single, checkable claim rather than two overlapping ones.
 */
export const STALE_TIME_MS = 24 * 60 * 60_000;

/**
 * The options every audit-spend query is built from. Guarded by
 * `fetch-policy.test.ts` — re-introducing an interval here is a one-word
 * change that breaks nothing, reviews clean, and shows up only on a GCP bill,
 * so the test is the thing that reports it rather than a person noticing.
 */
export const AUDIT_SPEND_FETCH_POLICY = {
  refetchInterval: false,
  refetchOnWindowFocus: true,
  staleTime: STALE_TIME_MS,
} as const;
